import type { DraggableAttributes, DraggableSyntheticListeners } from '@dnd-kit/core';

/**
 * Chunk `C2`, agent D (`FE-11.2`).
 *
 * The geometry of a list drag, as pure functions, plus the shared `DragHandle`
 * type. Signatures are fixed by `docs/design/c2/C2-BRIEF.md` §6.2.
 *
 * Why this is a separate file from `BoardFace.tsx`: `C3` reorders CARDS across
 * the same shape of wire (`previousCardId` / `nextCardId`), and `fe-plan.md`
 * is explicit about what happens when two chunks nine apart each improvise the
 * same helper — they do not agree. The functions here are generic over ids for
 * that reason. `C3` imports them; it does not write its own.
 *
 * ⚠ NO RANKS. `neighboursForMove` returns the two ids FLANKING the gap, and
 * that pair is the whole request body. See `lib/lists.ts`'s header and brief
 * §3.1 for why the client is not allowed to compute a position.
 */

/**
 * What a sortable item hands to the header it is dragged by.
 *
 * Agent E produces it from `useSortable` in `SortableListColumn`; agent C
 * consumes it in `ListHeader`. It is a type rather than a prop-drill of four
 * loose values so that the two files cannot disagree about arity.
 *
 * ⚠ `attributes` and `setActivatorNodeRef` belong on a DEDICATED handle control
 * — brief §7.3. Spreading `attributes` onto the header container puts
 * `role="button"` on a `<div>` that already holds an `<h2>`, a count and a menu
 * trigger, which is a control containing controls: the exact shape of C1's
 * `<button>`-inside-`<a>` defect.
 */
export interface DragHandle {
  readonly setActivatorNodeRef: (element: HTMLElement | null) => void;
  /**
   * ⚠ dnd-kit's own types, not `Record<string, unknown>`, and the parent
   * changed this at integration rather than leaving the workaround it forced.
   *
   * `DraggableAttributes` is an **interface**, and an interface has no implicit
   * index signature, so it is not assignable to `Record<string, unknown>` —
   * TS2322, measured rather than recalled. Both consumers had independently
   * reached for `{ ...attributes }` and a cast back to element props to get
   * around it, which is two files casting away the same real type to satisfy a
   * looser one this file chose. The looser type bought nothing: `C3` reuses
   * `neighboursForMove`/`reorderIds`, which are generic over ids and touch
   * neither of these fields.
   */
  readonly attributes: DraggableAttributes;
  /** `DraggableSyntheticListeners` is already `… | undefined` — dnd-kit's own spelling. */
  readonly listeners: DraggableSyntheticListeners;
  readonly isDragging: boolean;
}

export interface MoveNeighbours {
  readonly previousId: string | null;
  readonly nextId: string | null;
}

export interface NeighboursForMoveOptions {
  /**
   * Ids that may never be RETURNED as a neighbour — the optimistic placeholders
   * of brief §5.2. A placeholder id sent to the server as `previousListId` is a
   * 404 nobody can trace back to a drag.
   */
  readonly excludeIds?: readonly string[];
}

/**
 * The order after moving `activeId` to the slot `overId` currently occupies.
 *
 * Pure array arithmetic; no ranks, ever. Mirrors `@dnd-kit/sortable`'s own
 * `arrayMove` two-step splice exactly (remove `activeId`, then reinsert at
 * `overId`'s ORIGINAL index against the now-shortened array) so that the order
 * this function produces is the same order dnd-kit's `SortableContext` already
 * rendered mid-drag — a caller that reordered differently here would show one
 * order while dragging and commit a different one on drop.
 *
 * One consequence worth stating because it surprises on first read: dragging
 * an item PAST another (`activeIndex < overIndex`) lands it immediately AFTER
 * `overId`, not before — the shortened-array reinsertion is what produces
 * that. Dragging BACKWARD (`activeIndex > overIndex`) lands it immediately
 * BEFORE `overId`. Both are exactly what a person watching the strip mid-drag
 * already saw happen.
 *
 * Ids absent from `orderedIds`, or `activeId === overId`, are a no-op: the
 * array is returned unchanged (a copy).
 */
export function reorderIds(
  orderedIds: readonly string[],
  activeId: string,
  overId: string,
): string[] {
  const activeIndex = orderedIds.indexOf(activeId);
  const overIndex = orderedIds.indexOf(overId);

  if (activeIndex === -1 || overIndex === -1 || activeIndex === overIndex) {
    return [...orderedIds];
  }

  const result = [...orderedIds];
  const [moved] = result.splice(activeIndex, 1);
  result.splice(overIndex, 0, moved!);
  return result;
}

/**
 * The pair flanking the gap `activeId` lands in, computed against the order at
 * drop time.
 *
 * ⚠ `orderedIds` MUST be the order as rendered BEFORE this move is applied —
 * the same array `SortableContext` was given for the drag that just ended, not
 * one you have already spliced or optimistically reordered. This function
 * removes `activeId` and re-lands it itself (via `reorderIds`); handing it an
 * already-moved array double-applies the move and every drop lands one slot
 * further than intended, silently. (For whoever reads the e2e spec expecting
 * this to be caught there: `FR-BOARD-011` AC1's fourth-to-first-position case
 * cannot distinguish the two readings — the result is identical either way —
 * so that assertion alone does not guard this precondition.)
 *
 * Returns `null` for a no-op — `activeId === overId`, either id absent from
 * `orderedIds`, or **the computed pair is `{ previousId: null, nextId: null }`**
 * (every candidate neighbour on both sides was in `excludeIds`, so nothing
 * flanks the gap at all). That last case matters on the wire and not just for
 * symmetry: `listPositionPatchSchema` refuses both-null with a 422 — it is the
 * one pair `PATCH /lists/:id/position` cannot accept — so returning it here
 * would hand a caller a value it cannot send. A no-op sends no request at all.
 *
 * ⚠ The active id is removed from the order BEFORE the neighbours are read:
 * concretely, this computes `reorderIds(orderedIds, activeId, overId)` (which
 * itself starts by removing `activeId`) and reads the two ids either side of
 * `activeId`'s index in THAT array. Getting this off by one produces a board
 * that reorders almost correctly, which is the worst available failure: it
 * looks like a race, not like an arithmetic bug.
 *
 * `excludeIds` never changes where the gap is computed — a placeholder still
 * occupies its rendered slot for that purpose — it only filters what this
 * function is willing to hand back as `previousId`/`nextId`. If the immediate
 * neighbour on a side is excluded, this walks further out on that side rather
 * than returning `null` for that side, so a real neighbour is still reported
 * whenever one exists.
 *
 * ⚠ CALLERS MUST PASS `excludeIds` NAMING EVERY CURRENTLY-OPTIMISTIC LIST ID
 * (`isOptimisticListId` from `lib/lists.ts`, brief §5.2 row 3) whenever a
 * placeholder could be adjacent to the drop. This function has no way to
 * detect a placeholder itself — ids are opaque strings to it, by design, so
 * `C3` can reuse it for cards without importing anything list-specific — so an
 * omitted `excludeIds` is a silent path to sending a placeholder id as
 * `previousListId`/`nextListId`, a 404 with nothing pointing back at the drag
 * that caused it.
 */
export function neighboursForMove(
  orderedIds: readonly string[],
  activeId: string,
  overId: string,
  options?: NeighboursForMoveOptions,
): MoveNeighbours | null {
  if (activeId === overId) {
    return null;
  }
  if (!orderedIds.includes(activeId) || !orderedIds.includes(overId)) {
    return null;
  }

  const excludeIds = new Set(options?.excludeIds ?? []);
  const nextOrder = reorderIds(orderedIds, activeId, overId);
  const activeIndex = nextOrder.indexOf(activeId);

  let previousId: string | null = null;
  for (let i = activeIndex - 1; i >= 0; i -= 1) {
    const id = nextOrder[i]!;
    if (!excludeIds.has(id)) {
      previousId = id;
      break;
    }
  }

  let nextId: string | null = null;
  for (let i = activeIndex + 1; i < nextOrder.length; i += 1) {
    const id = nextOrder[i]!;
    if (!excludeIds.has(id)) {
      nextId = id;
      break;
    }
  }

  if (previousId === null && nextId === null) {
    return null;
  }

  return { previousId, nextId };
}

/**
 * Auto-scroll for the horizontal strip — `FR-BOARD-011` AC3.
 *
 * ⚠ FINDING (brief §6.2.1, and the parent's question #2 in §9): **configuration,
 * not code.** `DndContext` in `@dnd-kit/core` v6.3.1 (the version installed —
 * `apps/web/node_modules/@dnd-kit/core/package.json`) auto-scrolls by default
 * (`autoScroll = true` in `DndContext.tsx`'s props destructuring, read from
 * `core.esm.js`), and its `useAutoScroller` hook scans `scrollableAncestors` —
 * every scrollable ancestor of the dragged node, walked from nearest out — and
 * scrolls the first one for which `canScroll` does not refuse it. That scan is
 * axis-symmetric: `getScrollDirectionAndSpeed` (`core.esm.js`) computes `isLeft`
 * / `isRight` alongside `isTop` / `isBottom` and a `threshold.width` alongside
 * `threshold.height`, so a horizontally-overflowing ancestor auto-scrolls on the
 * x axis exactly as a vertically-overflowing one does on y — there is no
 * "vertical only" restriction anywhere in the implementation.
 *
 * The strip itself is that ancestor: `BoardFace.tsx` renders
 * `<ol id="board-lists" ...>` with `overflow-x-auto overflow-y-hidden`
 * (`BoardFace.tsx:217`), and every draggable list header lives inside it. So
 * the nearest scrollable ancestor `useAutoScroller` finds, walking up from a
 * dragged list's node, already IS the strip — no second scroll container has to
 * be built or wired for AC3 to fire.
 *
 * `DndContext`'s `autoScroll` prop accepts `boolean | AutoScrollOptions`
 * (`DndContext.d.ts`), and `AutoScrollOptions` is `{ acceleration?, activator?,
 * canScroll?, enabled?, interval?, layoutShiftCompensation?, order?, threshold?
 * }` (`useAutoScroller.d.ts`) — a strict superset of the shape below. So this
 * constant is not a parallel implementation; it is passed straight through as
 * `<DndContext autoScroll={STRIP_AUTO_SCROLL}>`, and dnd-kit's own scroller does
 * the rest. `threshold.y` is `0` because THIS STRIP never scrolls vertically
 * (`overflow-y-hidden`) — there is no vertical intent to detect — and `x`/
 * `acceleration` are left at dnd-kit's own defaults (`defaultThreshold = { x:
 * 0.2, y: 0.2 }`, `acceleration = 10` in `core.esm.js`) rather than invented
 * numbers, so a future tuning pass has one place to change rather than two
 * disagreeing defaults.
 *
 * ⚠ NOT VERBATIM-REUSABLE BY `C3`. This constant is specific to the
 * horizontal list strip. A card drag inside a list column scrolls
 * *vertically* (`ListColumn.tsx`'s card `<ol>` is `overflow-y-auto`), so `C3`
 * needs its own `{ threshold: { x: 0, y: 0.2 }, acceleration: … }` — the
 * mirror image of this one, not this one imported. What C3 SHOULD reuse
 * unchanged is `neighboursForMove` and `reorderIds` above; this constant is
 * the one export in this file that is strip-shaped, not id-generic.
 *
 * One traversal-order nuance for whoever tunes this later: `useAutoScroller`'s
 * default `order` is `TraversalOrder.TreeOrder`, which walks the outermost
 * scrollable ancestor first and the nearest one last, not nearest-first
 * (`core.esm.js`: `order === TraversalOrder.TreeOrder ?
 * [...scrollableAncestors].reverse() : scrollableAncestors`) — it does not
 * change the finding above, because `BoardFace`'s root carries
 * `overflow-hidden` (`BoardFace.tsx:26`), so `#board-lists` is the only
 * scrollable ancestor in the chain and traversal order has nothing else to
 * prefer over it.
 *
 * No scrolling code was written in this file, and none should be added here or
 * in `BoardFace.tsx` — see the finding above.
 */
export const STRIP_AUTO_SCROLL = {
  enabled: true,
  threshold: { x: 0.2, y: 0 },
  acceleration: 10,
} as const;
